/** Dados e ações da central de atendimento (Prompt 02). */
import type {
  EtiquetaResumo,
  ModeloMensagem,
  RespostaRapida,
  ResultadoBusca,
} from "@ydchat/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, ErroApi } from "./api";

const erro = (e: unknown, padrao: string) => toast.error(e instanceof ErroApi ? e.message : padrao);

export const useRespostasRapidas = () =>
  useQuery({
    queryKey: ["respostas-rapidas"],
    queryFn: () => api.get<RespostaRapida[]>("/respostas-rapidas"),
    staleTime: 60_000,
  });

export const useEtiquetas = () =>
  useQuery({
    queryKey: ["etiquetas"],
    queryFn: () => api.get<(EtiquetaResumo & { empresaId: string })[]>("/etiquetas"),
    staleTime: 5 * 60_000,
  });

export const useModelos = (conversaId: string, habilitado: boolean) =>
  useQuery({
    queryKey: ["modelos", conversaId],
    queryFn: () => api.get<ModeloMensagem[]>(`/conversas/${conversaId}/modelos`),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });

export const useBusca = (q: string) =>
  useQuery({
    queryKey: ["busca", q],
    queryFn: () => api.get<ResultadoBusca[]>(`/busca?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
    staleTime: 10_000,
  });

/** Envia arquivo (multipart). O api.ts só fala JSON. */
export async function enviarArquivo(
  conversaId: string,
  arquivo: Blob,
  opcoes: {
    idCliente: string;
    nome: string;
    legenda?: string;
    voz?: boolean;
    respostaAId?: string | null;
  },
) {
  const form = new FormData();
  form.set("arquivo", new File([arquivo], opcoes.nome, { type: arquivo.type }));
  form.set("idCliente", opcoes.idCliente);
  if (opcoes.legenda) form.set("legenda", opcoes.legenda);
  if (opcoes.voz) form.set("voz", "1");
  if (opcoes.respostaAId) form.set("respostaAId", opcoes.respostaAId);
  const resp = await fetch(`/api/conversas/${conversaId}/anexos`, {
    method: "POST",
    body: form,
    credentials: "same-origin",
  });
  if (!resp.ok) {
    const d = (await resp.json().catch(() => null)) as { erro?: string } | null;
    throw new ErroApi(resp.status, d?.erro ?? "Não foi possível enviar o arquivo.");
  }
  return (await resp.json()) as { mensagemId: string };
}

function useInvalidarConversa() {
  const qc = useQueryClient();
  return (id: string) => {
    void qc.invalidateQueries({ queryKey: ["conversas"] });
    void qc.invalidateQueries({ queryKey: ["conversa", id] });
  };
}

export function useAcoesCentral() {
  const invalidar = useInvalidarConversa();
  const concluir = useMutation({
    mutationFn: (id: string) => api.post(`/conversas/${id}/concluir`),
    onSuccess: (_r, id) => {
      toast.success("Atendimento concluído!");
      invalidar(id);
    },
    onError: (e) => erro(e, "Não foi possível concluir."),
  });
  const reabrir = useMutation({
    mutationFn: (id: string) => api.post(`/conversas/${id}/reabrir`),
    onSuccess: (_r, id) => invalidar(id),
    onError: (e) => erro(e, "Não foi possível reabrir."),
  });
  const acompanhar = useMutation({
    mutationFn: (v: { id: string; em: string | null; nota?: string }) =>
      api.post(`/conversas/${v.id}/acompanhar`, { em: v.em, nota: v.nota || null }),
    onSuccess: (_r, v) => {
      if (v.em) toast.success("Lembrete marcado!");
      invalidar(v.id);
    },
    onError: (e) => erro(e, "Não foi possível marcar o acompanhamento."),
  });
  const etiquetas = useMutation({
    mutationFn: (v: { id: string; etiquetaIds: string[] }) =>
      api.put(`/conversas/${v.id}/etiquetas`, { etiquetaIds: v.etiquetaIds }),
    onSuccess: (_r, v) => invalidar(v.id),
    onError: (e) => erro(e, "Não foi possível salvar as etiquetas."),
  });
  const encaminhar = useMutation({
    mutationFn: (v: {
      id: string;
      usuarioId: string;
      nome: string;
      comentario: string;
      mensagemId?: string | null;
    }) =>
      api.post(`/conversas/${v.id}/encaminhar`, {
        usuarioId: v.usuarioId,
        comentario: v.comentario,
        mensagemId: v.mensagemId ?? null,
      }),
    onSuccess: (_r, v) => {
      toast.success(`${v.nome.split(" ")[0]} foi avisada!`);
      invalidar(v.id);
    },
    onError: (e) => erro(e, "Não foi possível encaminhar."),
  });
  return { concluir, reabrir, acompanhar, etiquetas, encaminhar };
}

export function formatarTamanho(bytes: number | null): string {
  if (bytes === null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}
